import React, { useState } from 'react'
import { Box, Button, Card, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { C, SX, TYPE } from '../../merchants/shared/tokens'
import { DIFF_SX, tatDiff } from './tatDiff'

/** Lanes of the grid with this source (C = merchant custom, P = courier TAT), with names, by lane code. */
export const customLanes = (grid, source = 'C') => {
  const name = Object.fromEntries((grid?.zones || []).map((z) => [z.id, z.name || z.id]))
  return (grid?.cells || []).filter((c) => c.source === source).map((c) => ({
    ...c, lane: `${name[c.pickupZoneId] || c.pickupZoneId} to ${name[c.dropZoneId] || c.dropZoneId}`,
    diff: tatDiff(c.minTat, c.maxTat, c.standardMin, c.standardMax, c.baseSource === 'P' ? 'base' : 'standard'),
  }))
}

/**
 * One merchant's custom TAT lanes against its base (GET tat/grid cells with source C), with All / Faster / Slower /
 * Same MAX filters. Used by Step 2 TAT and the merchant TAT screen (plan screens 3 and 10). source='P' lists a courier's
 * own lanes against the standard (Courier TAT screen).
 *
 * @param baseLabel  column of the value under the lane: 'Standard', or the merchant's basis courier TAT
 * @param valueLabel column of the lane's own value: 'Custom' or the courier
 */
export default function CustomLanes({ grid, source = 'C', baseLabel = 'Standard', valueLabel = 'Custom' }) {
  const [filter, setFilter] = useState('all')
  const lanes = customLanes(grid, source)
  const shown = lanes.filter((l) => filter === 'all' || l.diff.tone === filter)
  return (
    <>
      <Box role='group' aria-label={`Filter ${valueLabel} lanes`} sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1.25 }}>
        {[['all', 'All'], ['faster', 'Faster'], ['slower', 'Slower'], ['same', 'Same MAX']].map(([k, label]) => {
          const on = filter === k
          const n = k === 'all' ? lanes.length : lanes.filter((l) => l.diff.tone === k).length
          return (
            <Button key={k} size='small' variant='outlined' aria-pressed={on} onClick={() => setFilter(k)}
              sx={{ ...SX.ctaGhost, fontSize: 12.5, minHeight: 32, ...(on && { color: C.accent, borderColor: C.accentBorder, bgcolor: C.accentSoft }) }}>
              {label} · {n}
            </Button>
          )
        })}
      </Box>
      <Card elevation={0} sx={{ ...SX.cardFlush, overflowX: 'auto' }}>
        <Table size='small' sx={{ minWidth: 560 }} aria-label={`${valueLabel} TAT lanes against the ${baseLabel.toLowerCase()}`}>
          <TableHead>
            <TableRow>
              {['Lane', baseLabel, valueLabel, `Against ${baseLabel.toLowerCase()}`].map((h) => (
                <TableCell key={h} sx={{ ...TYPE.eyebrow, fontSize: 10, color: C.inkFaint, bgcolor: C.surfaceAlt, borderColor: C.line, py: 1.25, whiteSpace: 'nowrap' }}>{h}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {shown.map((l) => (
              <TableRow key={l.zone} sx={{ '& td': { borderColor: C.lineSoft, py: 1 } }}>
                <TableCell>
                  <Typography sx={TYPE.rowLabel}>{l.lane}</Typography>
                  <Typography sx={{ ...TYPE.meta, color: C.inkFaint, fontFamily: 'JetBrains Mono, monospace' }}>{l.zone}</Typography>
                </TableCell>
                <TableCell sx={{ ...TYPE.value, fontVariantNumeric: 'tabular-nums', color: C.inkMuted }}>{l.standardMin}-{l.standardMax} days</TableCell>
                <TableCell sx={{ ...TYPE.value, fontVariantNumeric: 'tabular-nums' }}>{l.minTat}-{l.maxTat} days</TableCell>
                <TableCell><Box component='span' sx={{ ...SX.pill, ...DIFF_SX[l.diff.tone] }}>{l.diff.label}</Box></TableCell>
              </TableRow>
            ))}
            {shown.length === 0 && (
              <TableRow><TableCell colSpan={4} sx={{ ...TYPE.rowHelp, textAlign: 'center', py: 3 }}>No lane in this filter.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </>
  )
}
